// Tagihan Vendor — detail (#4b-3a layar 2, + panel Finance #4b-3b)
//
// Layar paling padat di sub-unit ini: EMPAT role melihat tombol berbeda.
//   ADMIN_PURCHASING : Ubah · Ajukan Ulang · Batalkan
//   KEPALA_CABANG    : Setujui · Tolak Pengajuan   (gerbang Q48)
//   OWNER            : sama dengan KC, dan satu-satunya yang boleh
//                      menyetujui nilai di atas batas region
//   FINANCE          : Tolak (saat eksekusi) · Catat Pembayaran (#4b-3b)
//
// NOL KONSTANTA BISNIS. Daftar status, transisi (termasuk dari status apa
// sebuah aksi boleh dijalankan), ambang panjang alasan, dan batas persetujuan
// SEMUANYA dibaca dari GET /purchasing/meta/invoice + /meta/payment. Tabel
// role di atas adalah CATATAN hasil membaca meta, bukan yang ditegakkan file
// ini.
//
// UI bukan keamanan — setiap aksi tetap dijaga role guard + state machine +
// RLS di backend. Di sini hanya supaya orang tidak menekan tombol yang pasti
// ditolak, dan tahu alasannya sebelum menekan.
//
// ANTREAN FINANCE = layar ini dengan filter status di daftar tagihan, BUKAN
// halaman tersendiri. Tagihan yang menunggu dibayar adalah tagihan yang sama,
// dilihat dari sudut lain; menduplikasi layarnya berarti dua tempat yang
// harus ikut berubah setiap kali aturan pembayaran bergeser.
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { StatusBadge, MetaStatusBadge } from "../../components/etams/StatusBadge";
import { BusyButton } from "../../components/etams/BusyButton";
import { VendorProofUploader } from "../../components/etams/VendorProofUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  ArrowLeft, RefreshCw, ReceiptText, ShieldCheck, AlertTriangle, FileWarning,
  Banknote, ExternalLink, Loader2, Info,
} from "lucide-react";
import { toast } from "sonner";
import { formatIDR, formatDate, formatDateTime, todayISO } from "../../lib/format";
import { useStoreTimezones } from "../../lib/timezone";
import { useAuth } from "../../contexts/AuthContext";
import { invoiceStatusBadge, sisaTagihanMeta } from "./VendorInvoices";
import {
  getInvoice, getInvoiceMeta, approveInvoice, rejectApprovalInvoice,
  rejectInvoice, resubmitInvoice, updateInvoice, cancelInvoice,
  ajukanPembatalanInvoice, putusPembatalanInvoice,
  getPaymentMeta, getPayment, createPayment, uploadPaymentProof,
  apiErrorMessage,
} from "../../lib/purchasingApi";
import Hint from "../../components/etams/Hint";
import {
  alasanBatalTagihanTertahan, bolehTolakEksekusi as bolehTolakTahapEksekusi, pembatalanTagihan, potonganTagihanPemasok,
} from "../../lib/aksiPurchasing";

// Nama peran untuk kalimat layar — kode peran mentah tidak tampil (K200 butir 6).
const NAMA_PERAN = { OWNER: "Owner", KEPALA_CABANG: "Kepala Cabang", FINANCE: "Finance", ADMIN_PURCHASING: "Admin Purchasing" };

/** Transisi bernama dari blok `transitions` sebuah endpoint /meta — sumber
 *  tunggal untuk "aksi ini boleh dari status apa" dan "alasannya minimal
 *  berapa karakter".
 *
 *  Generik terhadap SEMUA /meta yang berbentuk {transitions, permissions}:
 *  dipakai file ini untuk /meta/invoice DAN /meta/payment, dan dipakai ulang
 *  VendorPayments.jsx (#4b-3b). Namanya digeneralkan dari `transisiInvoice`
 *  saat pemakai keduanya muncul — bukan disalin jadi versi kedua. */
export function transisiMeta(meta, action) {
  return (meta?.transitions || []).find(t => t.action === action) || null;
}

/** Boleh tidaknya sebuah aksi: wewenang (permissions) DAN status asal
 *  (transitions[].from) — dua-duanya dari backend, nol daftar status di JS. */
export function bisaAksiMeta(meta, action, status) {
  const t = transisiMeta(meta, action);
  if (!t || !(meta?.permissions || {})[action]) return false;
  if (!Array.isArray(t.from)) return true;      // `from: null` = aksi create
  return t.from.includes(status);
}

export default function VendorInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { tzForRegion } = useStoreTimezones();
  const { profile } = useAuth();

  const [meta, setMeta] = useState(null);
  const [inv, setInv] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  //: satu dialog beralasan dipakai bertiga (reject_approval / reject /
  //: cancel) — bentuknya identik, hanya endpoint dan judulnya berbeda.
  const [alasanDlg, setAlasanDlg] = useState(null);   // {action,title,desc,run}
  const [reason, setReason] = useState("");
  const [approveOpen, setApproveOpen] = useState(false);
  const [setujuiBatalOpen, setSetujuiBatalOpen] = useState(false);   // K219

  //: dialog ubah/ajukan-ulang — dua-duanya mengirim InvoicePatchBody.
  const [formDlg, setFormDlg] = useState(null);       // "edit" | "resubmit"
  const [form, setForm] = useState({});

  // ---- Pembayaran (#4b-3b) --------------------------------------------
  const [payMeta, setPayMeta] = useState(null);
  const [bayarOpen, setBayarOpen] = useState(false);
  const [konfirmasiBayar, setKonfirmasiBayar] = useState(false);
  const [bayar, setBayar] = useState({});
  //: hasil unggah bukti — proof_file_id-nya yang ikut ke POST /payments.
  const [bukti, setBukti] = useState(null);
  //: id pembayaran yang sedang diambil signed URL-nya (bukti dibuka
  //: on-demand, daftar tidak ikut menerbitkan URL).
  const [buktiBusy, setBuktiBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getInvoice(id);
      setInv(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal memuat tagihan."));
      setInv(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    getInvoiceMeta()
      .then(r => setMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat info status tagihan.")));
    // /meta/payment dibaca 4 role yang sama dengan /meta/invoice — bukan
    // Finance saja. Yang membedakan bukan siapa yang boleh membacanya,
    // melainkan isi `permissions` yang dikirim balik untuk pemanggil itu.
    getPaymentMeta()
      .then(r => setPayMeta(r.data))
      .catch(e => toast.error(apiErrorMessage(e, "Gagal memuat aturan pembayaran.")));
  }, []);

  const jalankan = async (fn, okMsg, failMsg) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okMsg);
      setAlasanDlg(null);
      setApproveOpen(false);
      setSetujuiBatalOpen(false);
      setFormDlg(null);
      setReason("");
      // #4b-3b: dialog pembayaran ikut ditutup dari SATU tempat ini. Kalau
      // ditutup sendiri di pemanggilnya, cepat atau lambat ada jalur gagal
      // yang lupa menutupnya lalu bukti yang sudah terpakai tertinggal di
      // layar seolah masih bisa dikirim ulang.
      setKonfirmasiBayar(false);
      setBayarOpen(false);
      setBukti(null);
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, failMsg));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="inv-detail-loading">
        <div className="h-8 w-56 bg-muted rounded animate-pulse" />
        {[...Array(4)].map((_, i) =>
          <div key={i} className="h-12 bg-muted rounded animate-pulse" />)}
      </div>
    );
  }

  if (!inv) {
    return (
      <div className="p-12 text-center text-muted-foreground text-sm" data-testid="inv-detail-empty">
        Tagihan tidak ditemukan — mungkin sudah dibatalkan, atau bukan bagian
        region Anda.
        <div className="mt-3">
          <Button variant="outline" size="sm" onClick={() => navigate("/purchasing/invoices")}>
            Kembali ke daftar
          </Button>
        </div>
      </div>
    );
  }

  const tz = tzForRegion(inv.region_id || inv.region?.id);
  const b = invoiceStatusBadge(meta, inv.status);
  //: Sisa dihitung dengan RUMUS MILIK BACKEND (/meta/payment.remaining_formula),
  //: bukan tiga suku yang ditulis ulang di sini. Angka inilah yang jadi
  //: nominal bawaan dialog pembayaran — kalau ia salah, Finance kena
  //: OVERPAYMENT_BLOCKED atas angka yang tampak "sudah sesuai layar".
  const sisa = sisaTagihanMeta(inv, payMeta);
  const appr = meta?.approval || {};

  // Tiga peringatan di gerbang persetujuan. Semuanya HITUNGAN atas angka yang
  // dikirim backend — bukan aturan yang disalin ke JS.
  const pemohonSendiri = appr.requester_cannot_approve
    && !!profile?.id && inv.created_by === profile.id;

  // Batas approval diambil dari REGION INVOICE INI, yang ikut terkirim
  // bersama barisnya (Q74) — bukan dari meta.approval.caller_region_limit_*,
  // yang cakupannya region si PEMANGGIL. Perbedaannya bukan teoretis: Owner
  // dan Finance ber-region_id NULL, jadi memakai angka pemanggil membuat
  // layar ini memberi tahu Owner "batas region ini belum ditetapkan" untuk
  // region yang batasnya jelas terisi — dan peringatan "di atas batas"
  // tidak pernah muncul sama sekali.
  const batasRegion = inv.region?.payment_approval_limit_idr;
  const batasDiset = batasRegion !== null && batasRegion !== undefined;
  const diAtasBatas = batasDiset
    && Number(inv.total_amount) > Number(batasRegion);
  const batasBelumDiset = !batasDiset;

  const bolehSetujui       = bisaAksiMeta(meta, "approve", inv.status);
  const bolehTolakGerbang  = bisaAksiMeta(meta, "reject_approval", inv.status);
  const bolehTolakEksekusi = bolehTolakTahapEksekusi(bisaAksiMeta(meta, "reject", inv.status), inv);
  const bolehUbah          = bisaAksiMeta(meta, "edit", inv.status);
  const bolehAjukanUlang   = bisaAksiMeta(meta, "resubmit", inv.status);
  const bolehBatalkan      = bisaAksiMeta(meta, "cancel", inv.status);
  // Tagihan berpotongan (uang muka/retur/uang lebih) tidak bisa dibatalkan — tombolnya tetap tampil tapi tertahan
  // dengan alasan, supaya orang tahu jalan keluarnya (TAGIHAN-PEMASOK-BATAL-POTONGAN-HILANG-01).
  const alasanBatalTertahan = bolehBatalkan ? alasanBatalTagihanTertahan(inv) : null;
  const kalimatPotonganTetap = potonganTagihanPemasok(inv).total > 0
    ? " Potongan yang sudah ada (uang muka/retur/uang lebih) tetap menempel di tagihan ini." : "";
  // K219 / BUSINESS-94: tagihan berpotongan dibatalkan lewat pengajuan (Admin Purchasing → Kepala Cabang).
  const batal = pembatalanTagihan(inv, bisaAksiMeta(meta, "ajukan_pembatalan", inv.status),
                                  !!(meta?.permissions || {}).putus_pembatalan, profile?.id);
  const adaAksi = bolehSetujui || bolehTolakGerbang || bolehTolakEksekusi
    || bolehUbah || bolehAjukanUlang || bolehBatalkan || batal.bolehAjukan || batal.bolehPutus;

  const flowSteps = (meta?.statuses || []).filter(s => s.flow !== null)
    .sort((a, c) => a.flow - c.flow);
  const curFlow = (meta?.statuses || []).find(s => s.code === inv.status)?.flow ?? null;

  // ---- Pembayaran: wewenang & kelayakan, semuanya dari /meta/payment ----
  //
  // Dua syarat berbeda, sengaja dipisah supaya pesannya bisa berbeda:
  //   bolehCatatBayar  = pengguna ini berwenang mencatat pembayaran
  //   tagihanBisaDibayar = status tagihan ini memang menerima pembayaran
  // Yang kedua dibaca dari invoice_statuses_payable, BUKAN ditebak dari nama
  // status. Alasan penolakan untuk status lain juga sudah diumumkan backend
  // (invoice_unpayable_reasons) sehingga UI tidak perlu mengarang aturannya.
  const payPerm = payMeta?.permissions || {};
  const bolehCatatBayar = !!payPerm.create;
  const statusBisaDibayar = payMeta?.invoice_statuses_payable || null;
  const tagihanBisaDibayar = statusBisaDibayar
    ? statusBisaDibayar.includes(inv.status) : false;
  const alasanTakBisaDibayar =
    (payMeta?.invoice_unpayable_reasons || {})[inv.status] || null;
  const proofMeta = payMeta?.proof || null;
  const metodeList = payMeta?.payment_methods || [];

  //: Dibulatkan 2 desimal, SAMA dengan backend (round(..., 2) di
  //: create_payment). Tanpa itu, sisa hasil pengurangan float bisa jadi
  //: 4999999.999999999 dan "bayar penuh" ditolak layar sendiri sebagai
  //: kelebihan bayar — padahal backend justru menerimanya.
  const sisaBulat = Math.round(Number(sisa) * 100) / 100;
  const nominalBayar = Number(bayar.amount);
  const nominalSah = Number.isFinite(nominalBayar) && nominalBayar > 0;
  const lebihDariSisa = nominalSah && nominalBayar > sisaBulat;
  const buktiKurang = !!proofMeta?.required && !bukti?.proof_file_id;
  const bisaKirimBayar = nominalSah && !lebihDariSisa && !buktiKurang
    && !!bayar.payment_date && !!bayar.payment_method;

  const bukaDialogBayar = () => {
    setBukti(null);
    setBayar({
      payment_date: todayISO(),
      // Nominal bawaan = SELURUH sisa. Pembayaran penuh adalah kasus yang
      // paling sering; yang mencicil tinggal menurunkan angkanya.
      amount: sisaBulat > 0 ? String(sisaBulat) : "",
      payment_method: metodeList[0]?.code || "",
      bank_ref: "",
      notes: "",
    });
    setBayarOpen(true);
  };

  const kirimBayar = () => {
    jalankan(
      () => createPayment({
        invoice_id: inv.id,
        payment_date: bayar.payment_date,
        amount: nominalBayar,
        payment_method: bayar.payment_method,
        bank_ref: bayar.bank_ref?.trim() || null,
        notes: bayar.notes?.trim() || null,
        proof_file_id: bukti?.proof_file_id || null,
      }),
      "Pembayaran tercatat. Sisa tagihan diperbarui otomatis.",
      "Gagal mencatat pembayaran.",
    );
  };

  /** Bukti transfer dibuka on-demand: signed URL diminta saat diklik, tidak
   *  pernah ikut di daftar dan tidak pernah disimpan di state lama. */
  const lihatBukti = async (pid) => {
    setBuktiBusy(pid);
    try {
      const r = await getPayment(pid);
      const url = r.data?.proof?.signed_url;
      if (!url) {
        toast.error(
          "Bukti transfer tidak bisa dibuka sekarang. Coba lagi sebentar "
          + "lagi, atau laporkan ke Owner kalau berkasnya memang hilang."
        );
        return;
      }
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal menyiapkan tautan bukti transfer."));
    } finally {
      setBuktiBusy(null);
    }
  };

  const minAlasan = (action) => transisiMeta(meta, action)?.min_reason_length || 3;
  const alasanKurang = alasanDlg
    ? reason.trim().length < minAlasan(alasanDlg.action) : true;

  const bukaAlasan = (action, title, desc, run) => {
    setReason("");
    setAlasanDlg({ action, title, desc, run });
  };

  const bukaForm = (mode) => {
    setForm({
      invoice_number: inv.invoice_number || "",
      invoice_date: (inv.invoice_date || "").slice(0, 10),
      due_date: (inv.due_date || "").slice(0, 10),
      total_amount: inv.total_amount == null ? "" : String(inv.total_amount),
      notes: inv.notes || "",
    });
    setFormDlg(mode);
  };

  const kirimForm = () => {
    const body = {
      invoice_number: form.invoice_number?.trim() || null,
      invoice_date: form.invoice_date || undefined,
      due_date: form.due_date || null,
      notes: form.notes?.trim() || null,
    };
    const nominal = Number(form.total_amount);
    if (!Number.isFinite(nominal) || nominal <= 0) {
      toast.error("Nilai tagihan harus lebih dari nol.");
      return;
    }
    body.total_amount = nominal;
    if (formDlg === "resubmit") {
      jalankan(() => resubmitInvoice(inv.id, body),
        "Tagihan diajukan ulang — menunggu persetujuan lagi.",
        "Gagal mengajukan ulang tagihan.");
    } else {
      jalankan(() => updateInvoice(inv.id, body),
        "Perubahan tagihan tersimpan.",
        "Gagal menyimpan perubahan tagihan.");
    }
  };

  return (
    <div className="space-y-6" data-testid="inv-detail-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/purchasing/invoices")}
                  data-testid="inv-detail-back">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-2xl font-semibold flex items-center gap-2 flex-wrap">
              <ReceiptText className="h-6 w-6" /> {inv.internal_number}
              <StatusBadge tone={b.tone} testId="inv-detail-status">{b.label}</StatusBadge>
              {inv.is_overdue && (
                <StatusBadge tone="danger" testId="inv-detail-overdue">
                  Lewat jatuh tempo
                </StatusBadge>
              )}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              {b.description}
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="inv-detail-refresh">
          <RefreshCw className="h-4 w-4 mr-1" /> Muat ulang
        </Button>
      </div>

      {/* Progres alur — nomornya dari backend (`flow`), bukan urutan tebakan. */}
      {flowSteps.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="inv-detail-flow">
          {flowSteps.map(s => {
            const lewat = curFlow !== null && s.flow <= curFlow;
            return (
              <span key={s.code} title={s.description}
                    className={`text-xs px-2 py-1 rounded border ${
                      lewat
                        ? "bg-success-soft text-success-soft-foreground border-success/40"
                        : "text-muted-foreground border-border"}`}>
                {s.flow}. {s.label}
              </span>
            );
          })}
        </div>
      )}

      <Card className="p-4">
        <h2 className="text-sm font-medium mb-3">Ringkasan</h2>
        <dl className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">Vendor</dt>
            <dd className="font-medium">{inv.vendor?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Purchase Order</dt>
            <dd>
              {inv.po?.id ? (
                <button type="button" data-testid="inv-detail-po-link"
                        className="font-medium underline underline-offset-2 hover:text-foreground/80"
                        onClick={() => navigate(`/purchasing/po/${inv.po.id}`)}>
                  {inv.po.po_number}
                </button>
              ) : "—"}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Nomor dari vendor</dt>
            <dd>{inv.invoice_number || <span className="text-muted-foreground">Belum ada</span>}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Wilayah</dt>
            <dd>{inv.region?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Tanggal tagihan</dt>
            <dd>{formatDate(inv.invoice_date, tz)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Jatuh tempo</dt>
            <dd className={inv.is_overdue ? "text-danger-soft-foreground font-medium" : ""}>
              {inv.due_date ? formatDate(inv.due_date, tz)
                            : <span className="text-muted-foreground">Tunai (bayar langsung)</span>}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Nilai PO</dt>
            <dd>{formatIDR(inv.po?.total_amount)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Dibuat</dt>
            <dd>{formatDateTime(inv.created_at, tz)}</dd>
          </div>
        </dl>

        <div className="mt-4 pt-4 border-t grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <div>
            <dt className="text-muted-foreground text-xs">Nilai tagihan</dt>
            <dd className="text-lg font-semibold" data-testid="inv-detail-total">
              {formatIDR(inv.total_amount)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Sudah dibayar</dt>
            <dd>{formatIDR(inv.paid_amount)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground text-xs">Potongan uang muka</dt>
            <dd>{formatIDR(inv.dp_applied_amount)}</dd>
          </div>
          {/* K197 butir 3: retur yang diselesaikan potong tagihan (disetujui KC). */}
          <div>
            <dt className="text-muted-foreground text-xs">Potongan retur</dt>
            <dd data-testid="inv-potongan-retur">{formatIDR(inv.potongan_retur_idr || 0)}</dd>
          </div>
          {/* K215 / BUSINESS-90: uang lebih dari pemasok yang dipotongkan ke tagihan ini (disetujui KC). */}
          {Number(inv.potongan_uang_lebih_idr) > 0 && (
            <div>
              <dt className="text-muted-foreground text-xs">Potongan uang lebih pemasok</dt>
              <dd data-testid="inv-potongan-uang-lebih">{formatIDR(inv.potongan_uang_lebih_idr)}</dd>
            </div>
          )}
          {/* K197 butir 2: ongkir/biaya lain dicatat terpisah — bukan nilai barang (tidak ikut 3-way). */}
          {Number(inv.biaya_lain_idr) > 0 && (
            <div>
              <dt className="text-muted-foreground text-xs">Biaya lain (di dalam total)</dt>
              <dd data-testid="inv-biaya-lain">{formatIDR(inv.biaya_lain_idr)} — {inv.biaya_lain_keterangan}</dd>
            </div>
          )}
          <div>
            <dt className="text-muted-foreground text-xs">Sisa tagihan</dt>
            <dd className="text-lg font-semibold" data-testid="inv-detail-sisa">
              {formatIDR(sisa)}
            </dd>
          </div>
        </div>
        {inv.notes && (
          <p className="mt-4 pt-4 border-t text-sm">
            <span className="text-muted-foreground text-xs block">Catatan</span>
            {inv.notes}
          </p>
        )}
      </Card>

      {/* Jejak persetujuan & penolakan — dipertahankan backend, jangan
          disembunyikan setelah status berpindah. */}
      {(inv.approved_at || inv.rejected_at) && (
        <Card className="p-4" data-testid="inv-detail-jejak">
          <h2 className="text-sm font-medium mb-3">Jejak persetujuan</h2>
          <div className="space-y-2 text-sm">
            {inv.approved_at && (
              <div className="flex items-start gap-2">
                <ShieldCheck className="h-4 w-4 mt-0.5 text-success-soft-foreground shrink-0" />
                <span>
                  Disetujui {inv.approved_role_code ? `(${inv.approved_role_code})` : ""}{" "}
                  pada {formatDateTime(inv.approved_at, tz)}
                </span>
              </div>
            )}
            {inv.rejected_at && (
              <div className="flex items-start gap-2">
                <FileWarning className="h-4 w-4 mt-0.5 text-danger-soft-foreground shrink-0" />
                <span>
                  Ditolak pada {formatDateTime(inv.rejected_at, tz)}
                  {inv.reject_reason && (
                    <span className="block text-muted-foreground">
                      Alasan: {inv.reject_reason}
                    </span>
                  )}
                </span>
              </div>
            )}
          </div>
        </Card>
      )}

      {/* Pembayaran yang sudah tercatat. Sejak #4b-3b: bukti bisa dibuka
          (signed URL diminta saat diklik) dan Finance bisa mencatat
          pembayaran baru dari panel di bawah tabel ini. */}
      <Card className="overflow-hidden" data-testid="inv-detail-payments">
        <div className="p-4 pb-3 flex items-center justify-between gap-2 flex-wrap">
          <h2 className="text-sm font-medium">Pembayaran</h2>
          {(inv.payments || []).length > 0 && (
            <Button variant="ghost" size="sm" data-testid="inv-detail-riwayat"
                    onClick={() => navigate(
                      // `dari=` kosong = saringan bawaan "90 hari terakhir" sengaja
                      // dilepas: riwayat SATU tagihan harus lengkap, setua apa pun.
                      `/purchasing/payments?invoice_id=${inv.id}&dari=`)}>
              Riwayat lengkap
            </Button>
          )}
        </div>
        {(inv.payments || []).length === 0 ? (
          <p className="px-4 pb-6 text-sm text-muted-foreground flex items-center gap-1">
            Belum ada pembayaran untuk tagihan ini.
            <Hint>Pembayaran dicatat Finance setelah tagihan disetujui.</Hint>
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">Nomor</th>
                  <th className="px-4 py-2 text-left font-medium">Tanggal</th>
                  <th className="px-4 py-2 text-left font-medium">Metode</th>
                  <th className="px-4 py-2 text-right font-medium">Nominal</th>
                  <th className="px-4 py-2 text-left font-medium">Bukti</th>
                  <th className="px-4 py-2 text-left font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {(inv.payments || []).map(p => (
                  <tr key={p.id} data-testid={`inv-payment-${p.payment_number}`}>
                    <td className="px-4 py-2 font-medium">{p.payment_number}</td>
                    <td className="px-4 py-2">{formatDate(p.payment_date, tz)}</td>
                    <td className="px-4 py-2 text-muted-foreground">
                      {metodeList.find(m => m.code === p.payment_method)?.label
                        || p.payment_method || "—"}
                    </td>
                    <td className="px-4 py-2 text-right">{formatIDR(p.amount)}</td>
                    <td className="px-4 py-2">
                      {/* Pembayaran lama (sebelum bukti diwajibkan, Q49) sah
                          tidak punya bukti — tampilkan apa adanya, BUKAN
                          sebagai kesalahan. Yang ada buktinya jadi tombol:
                          signed URL diminta saat itu juga, tidak ikut di
                          respons daftar. */}
                      {p.proof_file_id ? (
                        <Button variant="ghost" size="sm" className="h-7 px-2"
                                disabled={buktiBusy === p.id}
                                data-testid={`inv-payment-proof-${p.payment_number}`}
                                onClick={() => lihatBukti(p.id)}>
                          {buktiBusy === p.id
                            ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                            : <ExternalLink className="h-3.5 w-3.5 mr-1" />}
                          Buka bukti
                        </Button>
                      ) : (
                        <StatusBadge tone="neutral">Tanpa bukti (data lama)</StatusBadge>
                      )}
                    </td>
                    <td className="px-4 py-2">
                      {/* #4b-3b: label & tone status pembayaran datang dari
                          /meta/payment lewat komponen bersama, bukan dari
                          pasangan if "CANCELLED : selain itu" yang ditulis
                          #4b-3a — status ketiga di backend akan tampil benar
                          tanpa file ini disentuh lagi. */}
                      <MetaStatusBadge meta={payMeta} code={p.status} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* --------------------- Panel Finance (#4b-3b) ---------------------- */}
      {bolehCatatBayar && (
        <Card className="p-4" data-testid="inv-detail-finance">
          <h2 className="text-sm font-medium mb-3 flex items-center gap-2">
            <Banknote className="h-4 w-4" /> Eksekusi pembayaran
          </h2>

          {!tagihanBisaDibayar ? (
            <div className="flex items-start gap-2 rounded border border-info/40
                            bg-info-soft p-3 text-sm text-info-soft-foreground"
                 data-testid="inv-pay-blocked">
              <Info className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Tagihan berstatus <strong>{b.label}</strong> belum bisa dibayar.
                {" "}{b.description}
                {alasanTakBisaDibayar && profile?.role?.code === "OWNER" && (
                  <span className="block text-xs mt-1 opacity-80">
                    Kode penolakan sistem kalau tetap dikirim:{" "}
                    {alasanTakBisaDibayar}.
                  </span>
                )}
              </span>
            </div>
          ) : sisaBulat <= 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="inv-pay-lunas">
              Tidak ada sisa yang harus ditransfer — tagihan ini sudah tertutup
              penuh oleh pembayaran dan/atau potongan uang muka.
            </p>
          ) : (
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <p className="text-sm text-muted-foreground">
                Sisa yang harus ditransfer ke{" "}
                <strong className="text-foreground">{inv.vendor?.name || "vendor ini"}</strong>:{" "}
                <strong className="text-foreground">{formatIDR(sisa)}</strong>.
                {proofMeta?.required && " Bukti transfer wajib dilampirkan."}
              </p>
              <BusyButton busy={busy} data-testid="inv-pay-btn"
                          onClick={bukaDialogBayar}>
                Catat Pembayaran
              </BusyButton>
            </div>
          )}
        </Card>
      )}

      {/* ------------------------------- Aksi ------------------------------ */}
      {adaAksi && (
        <Card className="p-4" data-testid="inv-detail-actions">
          <h2 className="text-sm font-medium mb-3">Tindakan</h2>

          {bolehSetujui && batasBelumDiset && (
            <div className="mb-3 flex items-start gap-2 rounded border border-warning/40 bg-warning-soft text-warning-soft-foreground p-3 text-sm"
                 data-testid="inv-warn-limit-unset">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Batas persetujuan pembayaran untuk region{" "}
                {inv.region?.name || "ini"} <strong>belum ditetapkan</strong>.
                Itu bukan berarti tanpa batas — persetujuan bisa ditolak
                sistem. Minta Owner mengisi batasnya lebih dulu.
              </span>
            </div>
          )}
          {bolehSetujui && diAtasBatas && (
            <div className="mb-3 flex items-start gap-2 rounded border border-warning/40 bg-warning-soft text-warning-soft-foreground p-3 text-sm"
                 data-testid="inv-warn-above-limit">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Nilai tagihan {formatIDR(inv.total_amount)} berada di atas batas
                persetujuan region {inv.region?.name || "ini"} (
                {formatIDR(batasRegion)}). Hanya{" "}
                {NAMA_PERAN[appr.above_limit_required_role || "OWNER"] || "Owner"} yang bisa
                menyetujuinya.
              </span>
            </div>
          )}
          {bolehSetujui && pemohonSendiri && (
            <div className="mb-3 flex items-start gap-2 rounded border border-danger/40 bg-danger-soft text-danger-soft-foreground p-3 text-sm"
                 data-testid="inv-warn-self-approval">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                Tagihan ini Anda sendiri yang mengajukan. Pemohon tidak boleh
                menyetujui pengajuannya sendiri — mintakan persetujuan ke orang
                lain yang berwenang.
              </span>
            </div>
          )}

          <div className="flex flex-wrap gap-2">
            {bolehSetujui && (
              <BusyButton busy={busy} disabled={pemohonSendiri}
                          data-testid="inv-approve-btn"
                          onClick={() => setApproveOpen(true)}>
                Setujui
              </BusyButton>
            )}
            {bolehTolakGerbang && (
              <BusyButton variant="destructive" busy={busy}
                          data-testid="inv-reject-approval-btn"
                          onClick={() => bukaAlasan(
                            "reject_approval",
                            "Tolak pengajuan pembayaran",
                            `Tagihan ${inv.internal_number} senilai ${formatIDR(inv.total_amount)} ` +
                            `untuk ${inv.vendor?.name || "vendor ini"} akan ditolak di gerbang ` +
                            `persetujuan. Admin Purchasing bisa memperbaiki lalu mengajukan ulang.` + kalimatPotonganTetap,
                            (r) => rejectApprovalInvoice(inv.id, { reason: r }))}>
                Tolak Pengajuan
              </BusyButton>
            )}
            {bolehTolakEksekusi && (
              <BusyButton variant="destructive" busy={busy}
                          data-testid="inv-reject-btn"
                          onClick={() => bukaAlasan(
                            "reject",
                            "Tolak tagihan",
                            `Tagihan ${inv.internal_number} senilai ${formatIDR(inv.total_amount)} ` +
                            `untuk ${inv.vendor?.name || "vendor ini"} akan ditolak. Admin Purchasing ` +
                            `bisa memperbaiki lalu mengajukan ulang.` + kalimatPotonganTetap,
                            (r) => rejectInvoice(inv.id, { reason: r }))}>
                Tolak
              </BusyButton>
            )}
            {bolehUbah && (
              <BusyButton variant="outline" busy={busy} data-testid="inv-edit-btn"
                          onClick={() => bukaForm("edit")}>
                Ubah
              </BusyButton>
            )}
            {bolehAjukanUlang && (
              <BusyButton busy={busy} data-testid="inv-resubmit-btn"
                          onClick={() => bukaForm("resubmit")}>
                Perbaiki &amp; Ajukan Ulang
              </BusyButton>
            )}
            {bolehBatalkan && (
              <BusyButton variant="outline" busy={busy} data-testid="inv-cancel-btn"
                          disabled={!!alasanBatalTertahan}
                          onClick={() => bukaAlasan(
                            "cancel",
                            "Batalkan tagihan",
                            `Tagihan ${inv.internal_number} senilai ${formatIDR(inv.total_amount)} ` +
                            `akan dibatalkan. Pembatalan hanya bisa selama belum ada pembayaran masuk.`,
                            (r) => cancelInvoice(inv.id, { reason: r }))}>
                Batalkan
              </BusyButton>
            )}
            {batal.bolehAjukan && (
              <BusyButton variant="outline" busy={busy} data-testid="inv-ajukan-batal-btn"
                          onClick={() => bukaAlasan(
                            "ajukan_pembatalan",
                            "Ajukan pembatalan tagihan",
                            `Tagihan ${inv.internal_number} sudah dipotong ${formatIDR(potonganTagihanPemasok(inv).total)} ` +
                            `(uang muka/retur/uang lebih). Bila Kepala Cabang menyetujui, tagihan dibatalkan dan ` +
                            `potongannya dilepas ke asalnya: uang muka bisa dipotongkan ke tagihan lain, retur dan ` +
                            `uang lebih bisa dipilih ulang penyelesaiannya. Tulis alasannya (mis. tagihan ganda, salah pemasok).`,
                            (r) => ajukanPembatalanInvoice(inv.id, { alasan: r }))}>
                Ajukan Pembatalan
              </BusyButton>
            )}
            {batal.bolehPutus && (
              <BusyButton busy={busy} data-testid="inv-setujui-batal-btn"
                          onClick={() => setSetujuiBatalOpen(true)}>
                Setujui Pembatalan
              </BusyButton>
            )}
            {batal.bolehPutus && (
              <BusyButton variant="destructive" busy={busy} data-testid="inv-tolak-batal-btn"
                          onClick={() => bukaAlasan(
                            "putus_pembatalan",
                            "Tolak pengajuan pembatalan",
                            `Tagihan ${inv.internal_number} tetap berlaku dan potongannya tetap menempel. ` +
                            `Admin Purchasing diberi tahu alasannya.`,
                            (r) => putusPembatalanInvoice(batal.menunggu.id, { setuju: false, alasan: r }))}>
                Tolak Pembatalan
              </BusyButton>
            )}
          </div>
          {alasanBatalTertahan && (
            <p className="text-xs text-muted-foreground mt-3" data-testid="inv-cancel-tertahan">
              {alasanBatalTertahan}
            </p>
          )}
          {bolehUbah && (
            <p className="text-xs text-muted-foreground mt-3">
              {transisiMeta(meta, "edit")?.conditions
                && `Syarat: ${transisiMeta(meta, "edit").conditions}.`}
            </p>
          )}
        </Card>
      )}

      {/* ------------------- Riwayat pengajuan pembatalan (K219) ------------------- */}
      {batal.riwayat.length > 0 && (
        <Card className="p-4" data-testid="inv-pembatalan">
          <h2 className="text-sm font-medium mb-3">Pengajuan pembatalan</h2>
          <ul className="space-y-3">
            {batal.riwayat.map((p) => (
              <li key={p.id} className="text-sm border rounded p-3" data-testid={`inv-pembatalan-${p.id}`}>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusBadge tone={p.status === "DISETUJUI" ? "success" : p.status === "DITOLAK" ? "danger" : "warning"}>
                    {p.status_label}
                  </StatusBadge>
                  <span className="text-xs text-muted-foreground">diajukan {formatDateTime(p.diajukan_pada)}</span>
                </div>
                <p className="mt-2">Alasan: {p.alasan}</p>
                {p.alasan_tolak && <p className="mt-1 text-muted-foreground">Alasan ditolak: {p.alasan_tolak}</p>}
                {p.rincian_teks && <p className="mt-1 text-muted-foreground">{p.rincian_teks}</p>}
              </li>
            ))}
          </ul>
          {batal.menunggu && !batal.bolehPutus && (
            <p className="text-xs text-muted-foreground mt-3">
              Menunggu keputusan Kepala Cabang cabang tagihan ini. Pengaju tidak bisa memutus pengajuannya sendiri.
            </p>
          )}
        </Card>
      )}

      {/* ------------------- Dialog setujui pembatalan (K219) ------------------- */}
      <Dialog open={setujuiBatalOpen} onOpenChange={(o) => !busy && setSetujuiBatalOpen(o)}>
        <DialogContent data-testid="inv-setujui-batal-dialog">
          <DialogHeader>
            <DialogTitle>Setujui pembatalan tagihan</DialogTitle>
            <DialogDescription>
              Tagihan {inv.internal_number} ({formatIDR(inv.total_amount)}, {inv.vendor?.name || "vendor ini"}) akan
              dibatalkan. Potongannya dilepas ke asalnya: uang muka {formatIDR(potonganTagihanPemasok(inv).uangMuka)},
              retur {formatIDR(potonganTagihanPemasok(inv).retur)}, uang lebih {formatIDR(potonganTagihanPemasok(inv).uangLebih)}.
              Alasan pengaju: {batal.menunggu?.alasan || "—"}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSetujuiBatalOpen(false)} disabled={busy}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Menyetujui..." data-testid="inv-setujui-batal-confirm"
                        onClick={() => jalankan(
                          () => putusPembatalanInvoice(batal.menunggu.id, { setuju: true }),
                          "Pembatalan disetujui — potongan dilepas ke asalnya.",
                          "Gagal menyetujui pembatalan.")}>
              Setujui Pembatalan
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------------------------- Dialog setujui ------------------------- */}
      <Dialog open={approveOpen} onOpenChange={(o) => !busy && setApproveOpen(o)}>
        <DialogContent data-testid="inv-approve-dialog">
          <DialogHeader>
            <DialogTitle>Setujui pengajuan pembayaran</DialogTitle>
            <DialogDescription>
              Setujui pembayaran {formatIDR(inv.total_amount)} ke{" "}
              {inv.vendor?.name || "vendor ini"} (tagihan {inv.internal_number},
              PO {inv.po?.po_number || "—"})? Setelah disetujui, tagihan masuk
              antrean Finance untuk dieksekusi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setApproveOpen(false)} disabled={busy}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Menyetujui..." data-testid="inv-approve-confirm"
                        onClick={() => jalankan(
                          () => approveInvoice(inv.id),
                          "Tagihan disetujui — masuk antrean Finance.",
                          "Gagal menyetujui tagihan.")}>
              Setujui
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* -------------------------- Dialog beralasan ----------------------- */}
      <Dialog open={!!alasanDlg} onOpenChange={(o) => !busy && !o && setAlasanDlg(null)}>
        <DialogContent data-testid="inv-reason-dialog">
          <DialogHeader>
            <DialogTitle>{alasanDlg?.title}</DialogTitle>
            <DialogDescription>{alasanDlg?.desc}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label className="text-sm font-medium">Alasan</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)}
                      data-testid="inv-reason-input" rows={3}
                      placeholder="Tuliskan alasannya supaya bisa ditindaklanjuti." />
            <p className="text-xs text-muted-foreground">
              Minimal {alasanDlg ? minAlasan(alasanDlg.action) : 3} karakter.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAlasanDlg(null)} disabled={busy}>
              Batal
            </Button>
            <BusyButton variant="destructive" busy={busy} busyText="Mengirim..."
                        disabled={alasanKurang} data-testid="inv-reason-confirm"
                        onClick={() => jalankan(
                          () => alasanDlg.run(reason.trim()),
                          "Tersimpan.",
                          "Gagal mengirim.")}>
              Kirim
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------------------ Dialog ubah / ajukan ulang ---------------- */}
      <Dialog open={!!formDlg} onOpenChange={(o) => !busy && !o && setFormDlg(null)}>
        <DialogContent data-testid="inv-form-dialog">
          <DialogHeader>
            <DialogTitle>
              {formDlg === "resubmit" ? "Perbaiki & ajukan ulang" : "Ubah tagihan"}
            </DialogTitle>
            <DialogDescription>
              {formDlg === "resubmit"
                ? "Tagihan akan kembali menunggu persetujuan Kepala Cabang/Owner. Jejak penolakan sebelumnya tetap tersimpan."
                : "Perubahan hanya bisa selagi tagihan belum disetujui dan belum ada pembayaran."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <label className="text-sm font-medium">Nomor dari vendor</label>
              <Input value={form.invoice_number || ""} data-testid="inv-form-number"
                     placeholder="Boleh dikosongkan kalau vendor belum menerbitkan"
                     onChange={(e) => setForm(f => ({ ...f, invoice_number: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Tanggal tagihan</label>
                <Input type="date" value={form.invoice_date || ""} data-testid="inv-form-date"
                       onChange={(e) => setForm(f => ({ ...f, invoice_date: e.target.value }))} />
              </div>
              <div>
                <label className="text-sm font-medium">Jatuh tempo</label>
                <Input type="date" value={form.due_date || ""} data-testid="inv-form-due"
                       onChange={(e) => setForm(f => ({ ...f, due_date: e.target.value }))} />
                <p className="text-xs text-muted-foreground mt-1">
                  Kosongkan kalau tunai (bayar langsung).
                </p>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium">Nilai tagihan (Rp)</label>
              <Input type="number" min="1" value={form.total_amount || ""}
                     data-testid="inv-form-total"
                     onChange={(e) => setForm(f => ({ ...f, total_amount: e.target.value }))} />
              <p className="text-xs text-muted-foreground mt-1">
                Nilai PO {inv.po?.po_number || "—"}: {formatIDR(inv.po?.total_amount)}.
                Total seluruh tagihan aktif PO ini tidak boleh melebihi nilai PO.
              </p>
            </div>
            <div>
              <label className="text-sm font-medium">Catatan</label>
              <Textarea value={form.notes || ""} rows={2} data-testid="inv-form-notes"
                        onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormDlg(null)} disabled={busy}>
              Batal
            </Button>
            <BusyButton busy={busy} busyText="Menyimpan..." data-testid="inv-form-submit"
                        onClick={kirimForm}>
              {formDlg === "resubmit" ? "Ajukan Ulang" : "Simpan"}
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ---------------------- Dialog catat pembayaran -------------------- */}
      {/* Titik paling berisiko di unit kerja ini: menyentuh UANG dan BERKAS
          sekaligus. Karena itu tiga lapis di sisi UI — validasi sebelum
          kirim, dialog konfirmasi yang menyebut nominal + vendor (D2), dan
          BusyButton di kedua tombol (D1). Penjaga sebenarnya tetap backend:
          OVERPAYMENT_BLOCKED, PAYMENT_PROOF_REQUIRED, PROOF_ALREADY_USED. */}
      <Dialog open={bayarOpen}
              onOpenChange={(o) => !busy && !konfirmasiBayar && !o && setBayarOpen(false)}>
        <DialogContent className="max-w-xl" data-testid="inv-pay-dialog">
          <DialogHeader>
            <DialogTitle>Catat pembayaran ke vendor</DialogTitle>
            <DialogDescription>
              Uang dianggap SUDAH keluar saat pembayaran ini dicatat — ini
              pencatatan, bukan perintah transfer. Tagihan{" "}
              {inv.internal_number} · {inv.vendor?.name || "vendor"} · sisa{" "}
              {formatIDR(sisa)}.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Nominal (Rp)</label>
                <Input type="number" min="1" value={bayar.amount || ""}
                       data-testid="inv-pay-amount"
                       onChange={(e) => setBayar(f => ({ ...f, amount: e.target.value }))} />
                <p className="text-xs text-muted-foreground mt-1">
                  Sisa tagihan {formatIDR(sisa)}
                  {payMeta?.remaining_formula
                    ? ` (${payMeta.remaining_formula})` : ""}.
                </p>
                {lebihDariSisa && (
                  <p className="text-xs text-danger-soft-foreground mt-1"
                     data-testid="inv-pay-over">
                    Nominal melebihi sisa tagihan. Turunkan ke {formatIDR(sisaBulat)}
                    {" "}atau kurang — sistem akan menolaknya.
                  </p>
                )}
                {bayar.amount !== "" && !nominalSah && (
                  <p className="text-xs text-danger-soft-foreground mt-1"
                     data-testid="inv-pay-amount-error">
                    Nominal harus lebih dari nol.
                  </p>
                )}
              </div>
              <div>
                <label className="text-sm font-medium">Tanggal bayar</label>
                <Input type="date" value={bayar.payment_date || ""}
                       data-testid="inv-pay-date"
                       onChange={(e) => setBayar(f => ({ ...f, payment_date: e.target.value }))} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-sm font-medium">Metode</label>
                <select value={bayar.payment_method || ""} data-testid="inv-pay-method"
                        onChange={(e) => setBayar(f => ({ ...f, payment_method: e.target.value }))}
                        className="mt-1 h-9 w-full rounded border bg-background px-2 text-sm">
                  {metodeList.length === 0 && <option value="">Memuat...</option>}
                  {metodeList.map(m =>
                    <option key={m.code} value={m.code}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Referensi bank</label>
                <Input value={bayar.bank_ref || ""} data-testid="inv-pay-bankref"
                       placeholder="Nomor referensi transfer (opsional)"
                       onChange={(e) => setBayar(f => ({ ...f, bank_ref: e.target.value }))} />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Catatan</label>
              <Textarea value={bayar.notes || ""} rows={2} data-testid="inv-pay-notes"
                        onChange={(e) => setBayar(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <VendorProofUploader
              proofMeta={proofMeta}
              disabled={busy}
              testIdPrefix="inv-pay-proof"
              uploadFn={(file) => uploadPaymentProof(file, inv.id).then(r => r.data)}
              onUploaded={setBukti}
            />
            {buktiKurang && (
              <p className="text-xs text-muted-foreground" data-testid="inv-pay-proof-hint">
                Unggah bukti transfernya lebih dulu — tombol lanjut baru aktif
                setelah berkasnya tersimpan.
              </p>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBayarOpen(false)} disabled={busy}>
              Batal
            </Button>
            <BusyButton busy={busy} disabled={!bisaKirimBayar}
                        data-testid="inv-pay-next"
                        onClick={() => setKonfirmasiBayar(true)}>
              Lanjut
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ------------- Konfirmasi pembayaran (D2 — sebut nominal) ---------- */}
      <Dialog open={konfirmasiBayar} onOpenChange={(o) => !busy && setKonfirmasiBayar(o)}>
        <DialogContent data-testid="inv-pay-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Catat pembayaran {formatIDR(nominalBayar)}?</DialogTitle>
            <DialogDescription>
              Pembayaran <strong>{formatIDR(nominalBayar)}</strong> ke{" "}
              <strong>{inv.vendor?.name || "vendor ini"}</strong> untuk tagihan{" "}
              {inv.internal_number} (PO {inv.po?.po_number || "—"}) akan
              dicatat sebagai uang yang sudah keluar, lengkap dengan bukti
              transfernya. Kalau nanti keliru, pembatalan dilakukan di layar
              Pembayaran Vendor dan wajib beralasan.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" disabled={busy}
                    onClick={() => setKonfirmasiBayar(false)}>
              Periksa lagi
            </Button>
            <BusyButton busy={busy} busyText="Menyimpan..."
                        data-testid="inv-pay-confirm" onClick={kirimBayar}>
              Catat Pembayaran
            </BusyButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
